import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { 
  RefreshCw, Undo2, Lightbulb, Check, Phone, 
  Clock, User, Settings, Users, Sparkles, X, ChevronRight, LogIn, ArrowLeft, Save, Plus, Trash2, Edit2, QrCode, Lock, Delete, ArrowUpDown, ChevronUp, ChevronDown, Calendar, ClipboardList, UserPlus
} from "lucide-react";
import { Toaster, toast } from "sonner";
import QRCode from 'react-qr-code';
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_PASSWORD = "1510";
const REORDER_PASSWORD = "2026";

// ============ PASSWORD MODAL ============
const PasswordModal = ({ isOpen, onClose, onSuccess, title, password }) => {
  const [inputPassword, setInputPassword] = useState('');
  const [error, setError] = useState('');

  // Reset input when modal opens - Fix for React StrictMode double-firing
  useEffect(() => {
    if (isOpen) {
      setInputPassword('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (inputPassword === password) {
      setInputPassword('');
      setError('');
      onSuccess();
    } else {
      setError('Wrong password');
      setInputPassword('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-3xl w-full max-w-xs p-6 shadow-2xl">
        <div className="text-center mb-4">
          <div className="w-14 h-14 bg-[#8B6F47] rounded-2xl flex items-center justify-center mx-auto mb-3">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h3 className="font-bold text-[#5C4A32] text-lg">{title}</h3>
          <p className="text-sm text-[#8B7355]">Enter password to continue</p>
        </div>
        
        <input
          key={isOpen ? 'modal-open' : 'modal-closed'}
          type="password"
          inputMode="numeric"
          placeholder="Password"
          value={inputPassword}
          onChange={e => { 
            const value = e.target.value;
            // Strict validation: only digits, max 4 chars
            if (/^\d{0,4}$/.test(value)) {
              setInputPassword(value); 
              setError(''); 
            }
          }}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          maxLength={4}
          className="w-full h-12 text-center text-xl font-bold border-2 border-[#D4B896] rounded-xl bg-[#FDF8F3] focus:border-[#8B6F47] outline-none transition-all"
          autoFocus
          data-testid="password-input"
        />
        
        {error && <p className="text-red-500 text-sm text-center mt-2">{error}</p>}
        
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => { onClose(); setInputPassword(''); setError(''); }}
            className="flex-1 py-3 bg-[#F5E6D3] text-[#6B5234] rounded-xl font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 py-3 bg-[#8B6F47] text-white rounded-xl font-semibold"
            data-testid="password-submit"
          >
            Enter
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ APPOINTMENT MODAL ============
const AppointmentModal = ({ isOpen, onClose, entry, onSet }) => {
  const [minutes, setMinutes] = useState(10);
  const [loading, setLoading] = useState(false);

  const handleSet = async () => {
    if (!entry) return;
    setLoading(true);
    try {
      await axios.post(`${API}/appointment/${entry.id}`, { minutes });
      toast.success(`Appointment set for ${entry.name}`);
      onSet();
      onClose();
    } catch (e) {
      toast.error("Failed to set appointment");
    }
    setLoading(false);
  };

  if (!isOpen || !entry) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#FDF8F3] rounded-3xl w-full max-w-sm shadow-2xl border border-[#D4B896]/30">
        <div className="bg-gradient-to-r from-[#E8A0BF] to-[#D4869C] px-5 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-white" />
            <span className="font-bold text-white text-lg">Set Appointment</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          <div className="text-center mb-4">
            <p className="text-[#5C4A32] font-semibold">{entry.name}</p>
            <p className="text-xs text-[#A68B5B]">#{entry.pager}</p>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-[#5C4A32]">Minutes from now:</label>
            <div className="flex gap-2">
              {[5, 10, 15, 30, 60].map(min => (
                <button
                  key={min}
                  onClick={() => setMinutes(min)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                    minutes === min
                      ? 'bg-[#E8A0BF] text-white'
                      : 'bg-[#F5E6D3] text-[#8B7355] border border-[#D4B896] hover:bg-[#E8D9C5]'
                  }`}
                >
                  {min}m
                </button>
              ))}
            </div>
            
            <input
              type="number"
              value={minutes}
              onChange={e => setMinutes(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full px-4 py-2 border-2 border-[#D4B896] rounded-lg bg-white focus:border-[#E8A0BF] outline-none"
              min="1"
            />
          </div>

          <div className="flex gap-2 mt-6">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-[#F5E6D3] text-[#6B5234] rounded-xl font-semibold hover:bg-[#E8D9C5]"
            >
              Cancel
            </button>
            <button
              onClick={handleSet}
              disabled={loading}
              className="flex-1 py-3 bg-[#E8A0BF] text-white rounded-xl font-semibold hover:bg-[#D4869C] disabled:opacity-50"
            >
              {loading ? 'Setting...' : 'Set Appointment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ TECH CLOCK-IN PAGE (Number Pad) ============
const TechClockIn = () => {
  const [pager, setPager] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [staff, setStaff] = useState([]);
  const [matchedName, setMatchedName] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successName, setSuccessName] = useState(''); // Store name for success modal

  // Load staff list on mount
  useEffect(() => {
    const loadStaff = async () => {
      try {
        const res = await axios.get(`${API}/staff`);
        setStaff(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    loadStaff();
  }, []);

  // Find matching staff name when pager changes
  useEffect(() => {
    if (pager) {
      const found = staff.find(s => s.pager === pager);
      setMatchedName(found ? found.name.toUpperCase() : '');
    } else {
      setMatchedName('');
    }
  }, [pager, staff]);

  const handleNumberPress = (num) => {
    if (pager.length < 4) {
      setPager(prev => prev + num);
      setMessage('');
    }
  };

  const handleDelete = () => {
    setPager(prev => prev.slice(0, -1));
    setMessage('');
  };

  const handleClockIn = async () => {
    if (!pager.trim()) {
      setMessage('Please enter your pager number');
      setMessageType('error');
      return;
    }
    if (!matchedName) {
      setMessage('Pager not found');
      setMessageType('error');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const res = await axios.post(`${API}/clock-in`, { pager: pager.trim() });
      setMessage(res.data.message);
      setMessageType('success');
      
      // Show success modal with tech name
      setSuccessName(matchedName);
      setShowSuccess(true);
      setPager('');
      
      // Auto-hide after 2 seconds
      setTimeout(() => {
        setShowSuccess(false);
        setSuccessName('');
      }, 2000);
    } catch (e) {
      setMessage(e.response?.data?.detail || "Clock in failed");
      setMessageType('error');
    }
    setLoading(false);
  };

  const NumberButton = ({ num, wide }) => (
    <button
      onClick={() => handleNumberPress(num)}
      className={`${wide ? 'col-span-2' : ''} h-16 bg-white border-2 border-[#D4B896] rounded-2xl text-2xl font-bold text-[#5C4A32] hover:bg-[#F5E6D3] active:scale-95 transition-all shadow-sm`}
      data-testid={`num-${num}`}
    >
      {num}
    </button>
  );

  return (
    <div className="min-h-screen bg-[url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200')] bg-cover bg-center bg-fixed">
      <div className="min-h-screen bg-[#F5EDE4]/95 backdrop-blur-sm flex flex-col">
        <Toaster position="top-center" richColors />
        
        {/* Simple Header - Empty */}
        <header className="bg-[#8B6F47]/90 backdrop-blur-md px-4 py-3 shadow-lg">
          <div className="h-6"></div>
        </header>

        {/* Main Content - Centered */}
        <main className="flex-1 flex items-center justify-center px-4 py-6">
          <div className="w-full max-w-xl space-y-6">
            
            {/* Staff Name Display - Separate box OUTSIDE main container */}
            {matchedName && (
              <div className="bg-gradient-to-r from-[#1B5E3C] to-[#145A32] rounded-3xl p-6 text-center animate-pulse shadow-2xl">
                <span className="text-5xl font-black text-white tracking-wide">
                  {matchedName}
                </span>
              </div>
            )}

            <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border-2 border-[#D4B896]/30">
              
              {/* Pager Number Display - No name inside */}
              <div className="mb-10">
                <div className="h-40 bg-gradient-to-br from-[#FDF8F3] to-[#F5E6D3] border-4 border-[#D4B896] rounded-3xl flex items-center justify-center shadow-inner">
                  <span className="text-8xl font-black text-[#5C4A32] tracking-widest">
                    {pager || <span className="text-[#D4B896]">---</span>}
                  </span>
                </div>
              </div>

              {/* Number Pad */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[1,2,3,4,5,6,7,8,9].map(num => (
                  <button
                    key={num}
                    onClick={() => handleNumberPress(String(num))}
                    className="h-24 bg-white border-4 border-[#D4B896] rounded-3xl text-4xl font-black text-[#5C4A32] hover:bg-[#F5E6D3] active:scale-95 transition-all shadow-xl"
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={handleDelete}
                  className="h-24 bg-[#F5E6D3] border-4 border-[#D4B896] rounded-3xl text-[#8B6F47] hover:bg-[#E8D9C5] active:scale-95 transition-all flex items-center justify-center shadow-xl"
                >
                  <Delete className="w-10 h-10" />
                </button>
                <button
                  onClick={() => handleNumberPress('0')}
                  className="h-24 bg-white border-4 border-[#D4B896] rounded-3xl text-4xl font-black text-[#5C4A32] hover:bg-[#F5E6D3] active:scale-95 transition-all shadow-xl"
                >
                  0
                </button>
                <button
                  onClick={handleClockIn}
                  disabled={loading || !pager || !matchedName}
                  className="h-24 bg-gradient-to-r from-[#1B5E3C] to-[#145A32] border-4 border-[#145A32] rounded-3xl text-white font-black text-3xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-40 disabled:from-[#8B7355] disabled:to-[#7A6244] disabled:border-[#7A6244] flex items-center justify-center shadow-xl"
                >
                  {loading ? <RefreshCw className="w-8 h-8 animate-spin" /> : 'OK'}
                </button>
              </div>

              {/* Message */}
              {message && (
                <div className={`p-4 rounded-xl text-center font-semibold text-base ${
                  messageType === 'success' 
                    ? 'bg-[#1B5E3C]/10 text-[#1B5E3C]' 
                    : 'bg-red-50 text-red-600'
                }`}>
                  {message}
                </div>
              )}
            </div>
          </div>
        </main>

        {/* Success Modal */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-3xl p-12 max-w-md w-full mx-4 shadow-2xl border-4 border-[#1B5E3C] animate-[fadeIn_0.3s_ease-in]">
              <div className="text-center space-y-6">
                {/* Green Checkmark */}
                <div className="w-32 h-32 mx-auto rounded-full bg-[#1B5E3C] flex items-center justify-center shadow-lg">
                  <svg className="w-20 h-20 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                {/* Success Message */}
                <div>
                  <h2 className="text-3xl font-black text-[#1B5E3C] mb-2">Clock In Thành Công!</h2>
                  <p className="text-xl text-[#5C4A32] font-semibold">
                    Chúc {successName} may mắn trong turn kế tiếp nhé ❤️
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ SERVICE HINT POPUP ============
const ServiceHintPopup = ({ isOpen, onClose, onSubmit }) => {
  const [selectedServices, setSelectedServices] = useState({});
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/service-categories`);
      setCategories(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleService = (category, service) => {
    setSelectedServices(prev => {
      if (prev[category] === service) {
        const { [category]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [category]: service };
    });
  };

  const quickSelect = (preset) => {
    if (preset === 'fullset') {
      setSelectedServices({ Hand: 'Acrylic Full Set' });
    } else if (preset === 'pedi') {
      setSelectedServices({ Feet: 'Pedicure' });
    } else if (preset === 'fullpedi') {
      setSelectedServices({ Hand: 'Acrylic Full Set', Feet: 'Pedicure' });
    }
  };

  const clearSelection = () => setSelectedServices({});

  const handleSubmit = async () => {
    const services = Object.values(selectedServices);
    if (services.length === 0) {
      toast.error("Please select at least 1 service");
      return;
    }
    setLoading(true);
    await onSubmit(services);
    setLoading(false);
    setSelectedServices({});
  };

  const selectedCount = Object.keys(selectedServices).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="service-hint-overlay">
      <div className="bg-[#FDF8F3] rounded-3xl w-full max-w-md max-h-[85vh] overflow-hidden shadow-2xl border border-[#D4B896]/30" data-testid="service-hint-popup">
        <div className="bg-gradient-to-r from-[#8B6F47] to-[#6B5234] px-5 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-[#F5E6D3]" />
            <span className="font-bold text-white text-lg">Service Hint</span>
            <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">{selectedCount}</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-colors" data-testid="close-hint-popup">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-2 p-4 border-b border-[#D4B896]/30">
          <button onClick={() => quickSelect('fullset')} className="flex-1 py-2.5 px-3 text-xs font-bold rounded-xl bg-[#5C4A32] text-white hover:bg-[#4a3a28] transition-colors" data-testid="quick-fullset">FULL SET</button>
          <button onClick={() => quickSelect('pedi')} className="flex-1 py-2.5 px-3 text-xs font-bold rounded-xl bg-[#8B6F47] text-white hover:bg-[#7a6040] transition-colors" data-testid="quick-pedi">PEDI</button>
          <button onClick={() => quickSelect('fullpedi')} className="flex-1 py-2.5 px-3 text-xs font-bold rounded-xl bg-[#A68B5B] text-white hover:bg-[#957a4a] transition-colors" data-testid="quick-fullpedi">FULL+PEDI</button>
        </div>

        <div className="p-4 max-h-[50vh] overflow-y-auto">
          {categories.map(category => {
            const isSelected = selectedServices[category.name];
            return (
              <div key={category.name} className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center text-xs ${isSelected ? 'bg-[#8B6F47] border-[#8B6F47] text-white' : 'border-[#A68B5B] text-[#A68B5B]'}`}>
                    {isSelected && <Check className="w-2.5 h-2.5" />}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#6B5234]">{category.name}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {category.services.map(service => (
                    <button
                      key={service}
                      onClick={() => toggleService(category.name, service)}
                      className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                        selectedServices[category.name] === service
                          ? 'bg-[#8B6F47] text-white shadow-md'
                          : 'bg-white text-[#6B5234] hover:bg-[#F5E6D3] border border-[#D4B896]'
                      }`}
                    >
                      {service}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-4 bg-white/50 border-t border-[#D4B896]/30 flex gap-3">
          <button onClick={clearSelection} className="flex-1 py-3 rounded-xl bg-[#F5E6D3] border border-[#D4B896] text-[#6B5234] font-semibold hover:bg-[#E8D9C5] transition-colors" data-testid="clear-selection">Clear</button>
          <button 
            onClick={handleSubmit}
            disabled={selectedCount === 0 || loading}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#8B6F47] to-[#6B5234] text-white font-semibold disabled:opacity-50 hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg"
            data-testid="submit-hint"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
            FIND
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ TECHNICIAN CARD ============
const TechCard = ({ entry, index, isHighlighted, blinkPhase, onAction, pending, appointment, onApptClick, walkinFlashName, onConfirmAppt }) => {
  const isWalkinFlash = walkinFlashName && (entry.name || "").toLowerCase().trim() === (walkinFlashName || "").toLowerCase().trim();
  const [elapsedTime, setElapsedTime] = useState('');
  const [apptCountdown, setApptCountdown] = useState('');

  useEffect(() => {
    if (!entry.start_time || entry.status === 'Available') {
      setElapsedTime('');
      return;
    }
    const updateTimer = () => {
      const start = new Date(entry.start_time).getTime();
      const now = Date.now();
      const diff = Math.floor((now - start) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      if (h > 0) setElapsedTime(`${h}h ${m}m`);
      else if (m > 0) setElapsedTime(`${m}m`);
      else setElapsedTime('1m');
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [entry.start_time, entry.status]);

  // Appointment countdown
  useEffect(() => {
    if (!appointment) {
      setApptCountdown('');
      return;
    }
    const updateAppt = () => {
      const apptTime = new Date(appointment.appointment_time).getTime();
      const now = Date.now();
      const diff = Math.floor((apptTime - now) / 1000);
      if (diff <= 0) {
        setApptCountdown('');
        return;
      }
      const m = Math.floor(diff / 60);
      const s = diff % 60;
      setApptCountdown(`${m}m`);
    };
    updateAppt();
    const interval = setInterval(updateAppt, 1000);
    return () => clearInterval(interval);
  }, [appointment]);

  const status = entry.status || 'Available';
  const hasAppt = !!appointment && apptCountdown;
  // Auto-appointment from Booking system (within 30 min)
  const autoApptMin = entry.next_appointment_minutes;
  const hasAutoAppt = typeof autoApptMin === 'number' && autoApptMin >= 0 && autoApptMin <= 30;
  const autoApptCustomer = entry.next_appointment_customer || '';
  
  const cardBgColors = {
    'Available': (hasAppt || hasAutoAppt) ? 'bg-[#FFD6D6] border-[#FFAAAA]' : 'bg-[#1B5E3C] border-[#145A32]',
    'Request': 'bg-[#B22222] border-[#8B1A1A]',
    'Done': 'bg-[#8B7355] border-[#7A6244]'
  };
  
  // Pink highlight blinks: on (phase 0, 2, 4), off (phase 1, 3)
  const showPink = isHighlighted && (blinkPhase === 0 || blinkPhase === 2 || blinkPhase === 4);

  // While the card is in HINT flash (isHighlighted, ~10s window), we let a
  // pure CSS keyframe handle the yellow blink so the background AND text
  // colors stay locked: yellow ↔ light-yellow bg, dark text the whole time.
  // This avoids the React blink-phase toggling that briefly re-applied the
  // green Available background + white text (which made the tech name appear
  // to "disappear" every other frame).
  const cardBg = isHighlighted
    ? 'hint-flash-card'  // CSS class — see App.css
    : (cardBgColors[status] || 'bg-white border-[#D4B896]');

  const isColored = status === 'Available' || status === 'Request' || status === 'Done';
  const textColor = isHighlighted
    ? 'text-[#1a1a1a]'   // force dark text for the entire 10s flash
    : (hasAppt || hasAutoAppt ? 'text-[#8B0000]' : (isColored ? 'text-white' : 'text-[#5C4A32]'));

  return (
    <div 
      className={`rounded-lg px-2 py-1.5 border transition-all duration-300 shadow-sm ${cardBg} ${
        showPink ? 'ring-2 ring-[#FF69B4]' : 'hover:shadow-md'
      } ${isWalkinFlash ? 'animate-walkin-flash ring-4 ring-yellow-400' : ''}`}
      data-testid={`tech-card-${entry.id}`}
    >
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-md flex items-center justify-center font-bold text-xs flex-shrink-0 ${
          isHighlighted ? 'bg-amber-700 text-white' : (showPink ? 'bg-[#FF69B4] text-white' : (isColored ? 'bg-white/25 text-white' : 'bg-zen-primary text-white'))
        }`}>
          {index + 1}
        </div>

        {/* Confirm button — only when a non-cancelled, non-CONFIRMED booking
            is within 30 min (and not past grace). After confirm: pink-blink
            stays on (until owner presses Walk/Appt or 5 min past start),
            but the ✓ disappears so reception doesn't tap twice. */}
        {hasAutoAppt && entry.next_appointment_booking_id && !entry.next_appointment_is_block && entry.next_appointment_status !== "confirmed" && (
          <button
            onClick={() => onConfirmAppt?.(entry.next_appointment_booking_id, entry.next_appointment_customer)}
            className="h-7 w-7 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center font-bold shadow-sm active:scale-95 transition-all flex-shrink-0 ring-2 ring-white"
            title={`Confirm appt — ${entry.next_appointment_customer}`}
            data-testid={`confirm-appt-${entry.id}`}
          >
            ✓
          </button>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`font-bold text-sm truncate ${textColor}`}>{entry.name}</span>

            {/* Pending Hint Display - Always Green */}
            {entry.pending_hint && (
              <span className="text-base ml-2 font-bold text-green-500">
                {entry.pending_hint.service}
              </span>
            )}
            {/* Time-budget leftover services — RED tags. Decay 60s after tech back to Available. */}
            {Array.isArray(entry.pending_services) && entry.pending_services.length > 0 && (
              <span className="inline-flex flex-wrap gap-1" data-testid={`pending-services-${entry.id}`}>
                {entry.pending_services.map((svc, i) => (
                  <span key={i}
                    className="text-[11px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-600 text-white shadow-sm tracking-wide animate-pulse">
                    {svc}
                  </span>
                ))}
              </span>
            )}
            {hasAutoAppt && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${autoApptMin <= 20 ? 'bg-[#C41E3A] text-white' : 'bg-white/95 text-[#C41E3A]'}`}
                    title={`Booking: ${autoApptCustomer}`}>
                APPT {autoApptMin}m
              </span>
            )}
            {pending && (
              <span className="text-xs font-bold text-[#90EE90] animate-pulse">
                {pending.join(', ')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {status === 'Available' ? (
              <span className={`text-[11px] ${isHighlighted ? 'text-[#1a1a1a]/80' : (showPink || hasAppt ? 'text-[#8B0000]/70' : 'text-white/70')} flex items-center gap-1`}>
                <Clock className="w-3 h-3" /> {entry.clock_in}
              </span>
            ) : elapsedTime ? (
              <span className={`text-[11px] font-semibold ${isHighlighted ? 'text-[#1a1a1a]/90' : (showPink || hasAppt ? 'text-[#8B0000]/90' : 'text-white/90')} flex items-center gap-1`}>
                <Clock className="w-3 h-3" /> {elapsedTime}
              </span>
            ) : (
              <span className={`text-[11px] ${isHighlighted ? 'text-[#1a1a1a]/80' : (showPink || hasAppt ? 'text-[#8B0000]/70' : (isColored ? 'text-white/70' : 'text-zen-muted'))}`}>{entry.clock_in}</span>
            )}
          </div>
        </div>

        <div className="flex gap-1 flex-shrink-0 items-center">
          {entry.ack === "ok" && (
            <span className="ack-rainbow text-[22px] font-extrabold tracking-wide px-2" data-testid={`ack-ok-${entry.id}`}>OK</span>
          )}
          {entry.ack === "no" && (
            <span className="text-[22px] font-extrabold tracking-wide px-2 text-black" data-testid={`ack-no-${entry.id}`}>NO</span>
          )}
          <button
            onClick={() => onAction(entry.id, 1)}
            className="h-9 px-3 rounded-md bg-[#0D3D23] text-white flex items-center justify-center hover:bg-[#0a2e1a] active:scale-95 transition-all shadow-sm text-[12px] font-bold"
            title="Walk"
            data-testid={`action-full-${entry.id}`}
          >
            Walk
          </button>
          <button
            onClick={() => onAction(entry.id, 2)}
            className="h-9 px-3 rounded-md bg-[#8B0000] text-white flex items-center justify-center hover:bg-[#6B0000] active:scale-95 transition-all shadow-sm text-[12px] font-bold"
            title="Appt"
            data-testid={`action-request-${entry.id}`}
          >
            Appt
          </button>
          <button
            onClick={() => onAction(entry.id, 3)}
            className="h-9 px-3 rounded-md bg-[#E91E8C] text-white flex items-center justify-center hover:bg-[#C91A7A] active:scale-95 transition-all shadow-sm"
            title="Call"
            data-testid={`action-call-${entry.id}`}
          >
            <Phone className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ REORDER MODAL ============
const ReorderModal = ({ isOpen, onClose, turnBoard, onReorder }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && turnBoard.length > 0) {
      setItems([...turnBoard]);
    }
  }, [isOpen]);

  const moveUp = (index) => {
    if (index === 0) return;
    const newItems = [...items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    setItems(newItems);
  };

  const moveDown = (index) => {
    if (index === items.length - 1) return;
    const newItems = [...items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    setItems(newItems);
  };

  const changeColor = async (index, newStatus) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], status: newStatus };
    setItems(newItems);
    
    // Immediately update backend without Telegram using dedicated status change endpoint
    try {
      await axios.post(`${API}/change-status/${newItems[index].id}/${newStatus.toLowerCase()}`, { 
        skipTelegram: true 
      });
    } catch (e) {
      console.error("Color change error:", e);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const orderIds = items.map(item => item.id);
      await axios.post(`${API}/reorder`, { order: orderIds });
      toast.success("Order updated!");
      onReorder();
      onClose();
    } catch (e) {
      toast.error("Failed to reorder");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#FDF8F3] rounded-3xl w-full max-w-md max-h-[85vh] overflow-hidden shadow-2xl border border-[#D4B896]/30">
        <div className="bg-gradient-to-r from-[#8B6F47] to-[#6B5234] px-5 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-5 h-5 text-[#F5E6D3]" />
            <span className="font-bold text-white text-lg">Reorder Turn</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 max-h-[55vh] overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-center text-[#8B7355] py-8">No one clocked in</p>
          ) : (
            <div className="space-y-2">
              {items.map((item, index) => (
                <div 
                  key={item.id} 
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 ${
                    item.status === 'Available' ? 'bg-[#1B5E3C]/10 border-[#1B5E3C]' :
                    item.status === 'Request' ? 'bg-[#B22222]/10 border-[#B22222]' :
                    'bg-[#8B7355]/10 border-[#8B7355]'
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg bg-[#8B6F47] text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <span className="font-bold text-[#5C4A32]">{item.name}</span>
                    <span className="text-xs text-[#8B7355] ml-2">#{item.pager}</span>
                    
                    {/* Color buttons */}
                    <div className="flex gap-1 mt-1">
                      <button
                        onClick={() => changeColor(index, 'Available')}
                        className="w-5 h-5 rounded bg-[#1B5E3C] hover:opacity-80"
                        title="Green (Available)"
                      />
                      <button
                        onClick={() => changeColor(index, 'Request')}
                        className="w-5 h-5 rounded bg-[#B22222] hover:opacity-80"
                        title="Red (Request)"
                      />
                      <button
                        onClick={() => changeColor(index, 'Busy')}
                        className="w-5 h-5 rounded bg-[#8B7355] hover:opacity-80"
                        title="Gray (Busy)"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="w-8 h-8 rounded-lg bg-[#D4B896] text-[#5C4A32] flex items-center justify-center hover:bg-[#C4A886] disabled:opacity-30 active:scale-95 transition-all"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index === items.length - 1}
                      className="w-8 h-8 rounded-lg bg-[#D4B896] text-[#5C4A32] flex items-center justify-center hover:bg-[#C4A886] disabled:opacity-30 active:scale-95 transition-all"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 bg-white/50 border-t border-[#D4B896]/30 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl bg-[#F5E6D3] border border-[#D4B896] text-[#6B5234] font-semibold hover:bg-[#E8D9C5] transition-colors">
            Cancel
          </button>
          <button 
            onClick={handleSave}
            disabled={loading}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#8B6F47] to-[#6B5234] text-white font-semibold disabled:opacity-50 hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Order
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ SERVICE MANAGEMENT POPUP ============
const ServiceManagement = ({ isOpen, onClose }) => {
  const [categories, setCategories] = useState([]);
  const [newService, setNewService] = useState({ category: '', name: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) loadCategories();
  }, [isOpen]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/service-categories`);
      setCategories(res.data);
    } catch (e) {
      toast.error("Failed to load");
    }
    setLoading(false);
  };

  const addService = async () => {
    if (!newService.category || !newService.name.trim()) {
      toast.error("Select category and enter service name");
      return;
    }
    try {
      await axios.post(`${API}/services`, { category: newService.category, service: newService.name.trim() });
      toast.success("Service added");
      setNewService({ category: '', name: '' });
      loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const deleteService = async (category, service) => {
    if (!window.confirm(`Delete "${service}"?`)) return;
    try {
      await axios.delete(`${API}/services`, { data: { category, service } });
      toast.success("Service deleted");
      loadCategories();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#FDF8F3] rounded-3xl w-full max-w-lg max-h-[85vh] overflow-hidden shadow-2xl border border-[#D4B896]/30">
        <div className="bg-gradient-to-r from-[#8B6F47] to-[#6B5234] px-5 py-4 flex justify-between items-center">
          <span className="font-bold text-white text-lg">Manage Services</span>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 border-b border-[#D4B896]/30 bg-white/50">
          <div className="flex gap-2">
            <select
              value={newService.category}
              onChange={e => setNewService(p => ({ ...p, category: e.target.value }))}
              className="flex-1 px-3 py-2 rounded-xl border border-[#D4B896] bg-white text-sm"
            >
              <option value="">Select Category</option>
              {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <input
              type="text"
              placeholder="Service name"
              value={newService.name}
              onChange={e => setNewService(p => ({ ...p, name: e.target.value }))}
              className="flex-1 px-3 py-2 rounded-xl border border-[#D4B896] bg-white text-sm"
            />
            <button onClick={addService} className="px-4 py-2 bg-[#8B6F47] text-white rounded-xl font-semibold hover:bg-[#7a6040]">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-4 max-h-[50vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[#8B6F47]" /></div>
          ) : (
            categories.map(cat => (
              <div key={cat.name} className="mb-4">
                <div className="text-xs font-bold uppercase tracking-wider text-[#6B5234] mb-2">{cat.name}</div>
                <div className="flex flex-wrap gap-2">
                  {cat.services.map(svc => (
                    <div key={svc} className="flex items-center gap-1 px-3 py-1.5 bg-white rounded-xl border border-[#D4B896] text-sm">
                      <span className="text-[#5C4A32]">{svc}</span>
                      <button onClick={() => deleteService(cat.name, svc)} className="text-red-400 hover:text-red-600 ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ============ STAFF MANAGEMENT ============
const StaffManagement = ({ isOpen, onClose }) => {
  const [staff, setStaff] = useState([]);
  const [skills, setSkills] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('staff');
  const [newStaff, setNewStaff] = useState({ name: '', pager: '', telegram_id: '' });
  const [editingStaff, setEditingStaff] = useState(null);
  const [editingSkills, setEditingSkills] = useState(null);
  const [tempSkills, setTempSkills] = useState([]);
  const [showQR, setShowQR] = useState(false);
  const [qrType, setQrType] = useState('tech');

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [staffRes, skillsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/staff`),
        axios.get(`${API}/skills`),
        axios.get(`${API}/service-categories`)
      ]);
      setStaff(staffRes.data);
      setSkills(skillsRes.data);
      const services = categoriesRes.data.flatMap(c => c.services);
      setAllServices(services);
    } catch (e) {
      console.error("Load data error:", e.response?.data || e.message);
      toast.error(`Failed to load data: ${e.response?.status || 'Network error'}. Please check your connection.`);
    }
    setLoading(false);
  };

  const addStaff = async () => {
    if (!newStaff.name || !newStaff.pager) {
      toast.error("Name and Pager required");
      return;
    }
    try {
      await axios.post(`${API}/staff`, newStaff);
      toast.success("Staff added");
      setNewStaff({ name: '', pager: '', telegram_id: '' });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const updateStaff = async () => {
    if (!editingStaff) return;
    try {
      await axios.put(`${API}/staff/${editingStaff.id}`, {
        name: editingStaff.name,
        pager: editingStaff.pager,
        telegram_id: editingStaff.telegram_id || ''
      });
      toast.success("Updated");
      setEditingStaff(null);
      loadData();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const deleteStaff = async (id) => {
    if (!window.confirm("Delete this staff?")) return;
    try {
      await axios.delete(`${API}/staff/${id}`);
      toast.success("Deleted");
      loadData();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const updateSex = async (staffId, sex) => {
    try {
      console.log(`Updating sex for staff ${staffId} to ${sex}`);
      const response = await axios.patch(`${API}/staff/${staffId}/sex?sex=${sex}`);
      console.log('Update sex response:', response.data);
      toast.success(`Updated to ${sex === 'male' ? '👨 Nam' : '👩 Nữ'}`);
      loadData();
    } catch (e) {
      console.error('Update sex error:', e);
      console.error('Error response:', e.response?.data);
      console.error('Error status:', e.response?.status);
      const errorMsg = e.response?.data?.detail || e.message || "Failed to update sex";
      toast.error(`Failed: ${errorMsg}`);
    }
  };

  const startEditSkills = (skill) => {
    setEditingSkills(skill.pager);
    setTempSkills([...skill.skills] || []);
  };

  const toggleTempSkill = (service) => {
    setTempSkills(prev => prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]);
  };

  const saveSkills = async () => {
    if (!editingSkills) return;
    try {
      await axios.put(`${API}/skills/${editingSkills}`, { skills: tempSkills });
      toast.success("Skills updated");
      loadData();
      setEditingSkills(null);
      setTempSkills([]);
    } catch (e) {
      toast.error("Failed");
    }
  };

  const cancelEditSkills = () => {
    setEditingSkills(null);
    setTempSkills([]);
  };

  const techClockInUrl = `${window.location.origin}/clock-in`;
  const ownerUrl = `${window.location.origin}/`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#FDF8F3] rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl border border-[#D4B896]/30">
        <div className="bg-gradient-to-r from-[#8B6F47] to-[#6B5234] px-5 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#F5E6D3]" />
            <span className="font-bold text-white text-lg">Staff Management</span>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => { setShowQR(true); setQrType('tech'); }}
              className="px-3 py-1.5 bg-white/20 text-white rounded-lg text-xs font-semibold hover:bg-white/30 flex items-center gap-1"
            >
              <QrCode className="w-4 h-4" /> Tech QR
            </button>
            <button 
              onClick={() => { setShowQR(true); setQrType('owner'); }}
              className="px-3 py-1.5 bg-[#F5D98E] text-[#5C4A32] rounded-lg text-xs font-semibold hover:bg-[#E8CC81] flex items-center gap-1"
              data-testid="owner-qr-btn"
            >
              <QrCode className="w-4 h-4" /> Owner QR
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {showQR && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-10">
            <div className="bg-white rounded-3xl p-6 text-center max-w-sm mx-4">
              <h3 className="font-bold text-[#5C4A32] mb-2">
                {qrType === 'tech' ? '👷 Tech Clock-In' : '👑 Owner Control'}
              </h3>
              <p className="text-xs text-[#8B7355] mb-4">
                {qrType === 'tech' ? 'For technicians to clock in only' : 'Full control: Turn Board + Hint + Settings'}
              </p>
              <div className="bg-white p-4 rounded-2xl inline-block border-4 border-[#D4B896]">
                <QRCode value={qrType === 'tech' ? techClockInUrl : ownerUrl} size={180} />
              </div>
              <p className="text-xs text-[#8B7355] mt-4 break-all">
                {qrType === 'tech' ? techClockInUrl : ownerUrl}
              </p>
              <button onClick={() => setShowQR(false)} className="mt-4 px-6 py-2 bg-[#8B6F47] text-white rounded-xl font-semibold">
                Close
              </button>
            </div>
          </div>
        )}

        <div className="flex border-b border-[#D4B896]/30">
          <button onClick={() => { setActiveTab('staff'); cancelEditSkills(); setEditingStaff(null); }} className={`flex-1 py-3 text-sm font-semibold transition-colors ${activeTab === 'staff' ? 'text-[#5C4A32] border-b-2 border-[#8B6F47]' : 'text-[#A68B5B]'}`}>
            <Users className="w-4 h-4 inline mr-1" /> Staff Info
          </button>
          <button onClick={() => { setActiveTab('skills'); setEditingStaff(null); }} className={`flex-1 py-3 text-sm font-semibold transition-colors ${activeTab === 'skills' ? 'text-[#5C4A32] border-b-2 border-[#8B6F47]' : 'text-[#A68B5B]'}`}>
            <Sparkles className="w-4 h-4 inline mr-1" /> Skills
          </button>
        </div>

        <div className="p-4 max-h-[55vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-[#8B6F47]" /></div>
          ) : activeTab === 'staff' ? (
            <div>
              <div className="bg-white/80 rounded-2xl p-4 mb-4 border border-[#D4B896]/30">
                <h3 className="font-semibold text-sm mb-3 text-[#5C4A32]">{editingStaff ? 'Edit Staff' : 'Add New Staff'}</h3>
                <div className="grid grid-cols-3 gap-2">
                  <input type="text" placeholder="Name" value={editingStaff ? editingStaff.name : newStaff.name} onChange={e => editingStaff ? setEditingStaff(p => ({ ...p, name: e.target.value })) : setNewStaff(p => ({ ...p, name: e.target.value }))} className="px-3 py-2 rounded-xl border border-[#D4B896] text-sm bg-[#FDF8F3]" />
                  <input type="text" placeholder="Pager #" value={editingStaff ? editingStaff.pager : newStaff.pager} onChange={e => editingStaff ? setEditingStaff(p => ({ ...p, pager: e.target.value })) : setNewStaff(p => ({ ...p, pager: e.target.value }))} className="px-3 py-2 rounded-xl border border-[#D4B896] text-sm bg-[#FDF8F3]" />
                  <input type="text" placeholder="Telegram ID" value={editingStaff ? (editingStaff.telegram_id || '') : newStaff.telegram_id} onChange={e => editingStaff ? setEditingStaff(p => ({ ...p, telegram_id: e.target.value })) : setNewStaff(p => ({ ...p, telegram_id: e.target.value }))} className="px-3 py-2 rounded-xl border border-[#D4B896] text-sm bg-[#FDF8F3]" />
                </div>
                <div className="flex gap-2 mt-3">
                  {editingStaff ? (
                    <>
                      <button onClick={() => setEditingStaff(null)} className="flex-1 py-2 bg-[#F5E6D3] text-[#6B5234] rounded-xl text-sm font-semibold">Cancel</button>
                      <button onClick={updateStaff} className="flex-1 py-2 bg-[#8B6F47] text-white rounded-xl text-sm font-semibold">Update</button>
                    </>
                  ) : (
                    <button onClick={addStaff} className="w-full py-2 bg-[#8B6F47] text-white rounded-xl text-sm font-semibold">Add Staff</button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                {staff.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 bg-white/80 border border-[#D4B896]/30 rounded-2xl">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#5C4A32]">{s.name}</span>
                        <span className="text-xs text-[#A68B5B]">#{s.pager}</span>
                        {s.telegram_id && <span className="text-xs text-[#8B6F47]">📱</span>}
                      </div>
                      <div className="flex gap-1 mt-2">
                        <button 
                          onClick={() => updateSex(s.id, 'male')}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                            s.sex === 'male' 
                              ? 'bg-blue-500 text-white shadow-md' 
                              : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                          }`}
                        >
                          👨 Nam
                        </button>
                        <button 
                          onClick={() => updateSex(s.id, 'female')}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                            s.sex === 'female' 
                              ? 'bg-pink-500 text-white shadow-md' 
                              : 'bg-pink-100 text-pink-600 hover:bg-pink-200'
                          }`}
                        >
                          👩 Nữ
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setEditingStaff(s)} className="text-xs text-[#8B6F47] hover:text-[#6B5234]"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => deleteStaff(s.id)} className="text-xs text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {skills.map(skill => {
                const isEditing = editingSkills === skill.pager;
                const displaySkills = isEditing ? tempSkills : (skill.skills || []);
                
                return (
                  <div key={skill.pager} className="bg-white/80 border border-[#D4B896]/30 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-[#5C4A32]">{skill.name} <span className="text-[#A68B5B]">#{skill.pager}</span></span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#A68B5B]">{displaySkills.length} skills</span>
                        {isEditing ? (
                          <>
                            <button onClick={cancelEditSkills} className="text-xs text-[#A68B5B] font-medium px-2 py-1">Cancel</button>
                            <button onClick={saveSkills} className="text-xs bg-[#1B5E3C] text-white font-medium px-3 py-1 rounded-lg flex items-center gap-1"><Save className="w-3 h-3" /> Save</button>
                          </>
                        ) : (
                          <button onClick={() => startEditSkills(skill)} className="text-xs text-[#8B6F47] font-medium px-2 py-1 bg-[#F5E6D3] rounded-lg">Edit</button>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {allServices.map(service => {
                        const isSelected = displaySkills.includes(service);
                        return (
                          <button key={service} onClick={() => isEditing && toggleTempSkill(service)} disabled={!isEditing} className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${isSelected ? 'bg-[#1B5E3C] text-white' : isEditing ? 'bg-[#F5E6D3] text-[#8B7355] border border-[#D4B896] hover:bg-[#E8D9C5] cursor-pointer' : 'bg-[#F5E6D3] text-[#8B7355] border border-[#D4B896] opacity-60'}`}>
                            {service}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ MANAGEMENT MODAL (Combined Staff + Service) ============
const ManagementModal = ({ isOpen, onClose, tab, onTabChange, turnBoard, onReorder }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#FDF8F3] rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl border border-[#D4B896]/30">
        <div className="bg-gradient-to-r from-[#8B6F47] to-[#6B5234] px-5 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-5 h-5 text-[#F5E6D3]" />
            <span className="font-bold text-white text-lg">Management</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#D4B896]/30 bg-white/50">
          <button
            onClick={() => onTabChange('staff')}
            className={`flex-1 py-3 px-4 font-semibold transition-all ${
              tab === 'staff'
                ? 'text-[#8B6F47] border-b-2 border-[#8B6F47] bg-white'
                : 'text-[#A68B5B] hover:bg-white/50'
            }`}
          >
            <Users className="w-4 h-4 inline-block mr-2" />
            Staff
          </button>
          <button
            onClick={() => onTabChange('reorder')}
            className={`flex-1 py-3 px-4 font-semibold transition-all ${
              tab === 'reorder'
                ? 'text-[#8B6F47] border-b-2 border-[#8B6F47] bg-white'
                : 'text-[#A68B5B] hover:bg-white/50'
            }`}
          >
            <ArrowUpDown className="w-4 h-4 inline-block mr-2" />
            Reorder
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {tab === 'staff' && <StaffManagement isOpen={true} onClose={onClose} />}
          {tab === 'reorder' && <ReorderModal isOpen={true} onClose={() => {}} turnBoard={turnBoard} onReorder={() => {}} />}
        </div>
      </div>
    </div>
  );
};

// ============ MAIN TURN BOARD (Owner Page) ============
const TurnBoard = () => {
  const [turnBoard, setTurnBoard] = useState([]);
  const [staff, setStaff] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [historyCount, setHistoryCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [highlightedId, setHighlightedId] = useState(null);
  const [blinkPhase, setBlinkPhase] = useState(0); // 0: on, 1: off, 2: on, then stop
  const [pendingServices, setPendingServices] = useState({});
  const [showHint, setShowHint] = useState(false);
  const [showStaffMgmt, setShowStaffMgmt] = useState(false);
  const [showServiceMgmt, setShowServiceMgmt] = useState(false);
  const [showReorder, setShowReorder] = useState(false);
  const [showAppt, setShowAppt] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showManagementModal, setShowManagementModal] = useState(false);
  const [managementTab, setManagementTab] = useState('staff'); // 'staff' or 'reorder'
  const [pendingAction, setPendingAction] = useState(null);
  const [currentPassword, setCurrentPassword] = useState(ADMIN_PASSWORD);
  const [countdown, setCountdown] = useState(null); // {hours, minutes, seconds}
  const [showManualReset, setShowManualReset] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [showClockIn, setShowClockIn] = useState(false);
  const [clockInPager, setClockInPager] = useState('');
  const [clockInBusy, setClockInBusy] = useState(false);
  // Walk-in flash signal from the parent Calendar (postMessage from Owner Dashboard)
  const [walkinFlashName, setWalkinFlashName] = useState(null);
  useEffect(() => {
    const handler = (ev) => {
      if (ev?.data?.type === "flash-staff" && ev.data.name) {
        setWalkinFlashName(ev.data.name);
        const dur = Math.min(20000, ev.data.durationMs || 10000);
        setTimeout(() => setWalkinFlashName(null), dur);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const handleClockInSubmit = async () => {
    const pager = clockInPager.trim();
    if (!pager) { toast.error('Enter a pager #'); return; }
    setClockInBusy(true);
    try {
      const res = await axios.post(`${API}/clock-in`, { pager });
      toast.success(res.data.message || 'Clocked in');
      setClockInPager('');
      setShowClockIn(false);
      await loadTurnBoard();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clock-in failed');
    } finally {
      setClockInBusy(false);
    }
  };

  const loadTurnBoard = useCallback(async () => {
    try {
      const [boardRes, staffRes, apptRes] = await Promise.all([
        axios.get(`${API}/turn-board`),
        axios.get(`${API}/staff`),
        axios.get(`${API}/appointments`)
      ]);
      setTurnBoard(boardRes.data.data);
      setHistoryCount(boardRes.data.historyCount);
      setStaff(staffRes.data);
      setAppointments(apptRes.data);
    } catch (e) {
      console.error("Load error:", e);
    }
  }, []);

  // Auto-reset at 9 PM Florida time + Countdown timer
  useEffect(() => {
    let autoResetFired = false; // prevent double-fire within same tick
    
    const updateCountdown = async () => {
      try {
        const res = await axios.get(`${API}/next-reset-time`);
        const seconds = res.data.seconds_until_reset;
        
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        
        setCountdown({ hours, minutes, seconds: secs });
        
        // AUTO-TRIGGER: when countdown hits 0 (or within 5s window),
        // simulate clicking Manual Reset and auto-typing password 1510
        if (seconds >= 0 && seconds <= 5 && !autoResetFired) {
          autoResetFired = true;
          console.log(`⏰ Countdown ended (${seconds}s) — firing auto manual-reset with password 1510`);
          
          try {
            // Call the SAME endpoint that the Manual Reset button uses,
            // with password 1510 as query param (matches "auto-type 1510" intent)
            const resetRes = await axios.post(`${API}/reset-board?password=1510`);
            
            toast.success("Board tự động reset lúc 9 PM (auto password 1510)");
            console.log("✅ Auto manual-reset success:", resetRes.data);
            await loadTurnBoard();
          } catch (e) {
            // Fallback: try the older check-auto-reset endpoint
            console.warn("Direct reset failed, trying check-auto-reset:", e?.response?.data);
            try {
              const alt = await axios.post(`${API}/check-auto-reset`);
              if (alt.data.should_reset) {
                toast.success("Board tự động reset (fallback)");
                await loadTurnBoard();
              }
            } catch (e2) {
              console.error("Auto-reset fallback also failed:", e2);
            }
          }
          
          // Release the lock after 30 seconds so next day can fire again
          setTimeout(() => { autoResetFired = false; }, 30000);
        }
      } catch (e) {
        console.error("Countdown error:", e);
      }
    };

    // Update immediately
    updateCountdown();

    // Update every second to ensure we don't miss the trigger
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [loadTurnBoard]);

  // Auto refresh every 5 seconds (pause when ANY modal is open to prevent data overwrite)
  useEffect(() => {
    // Don't refresh when any modal is open
    if (showReorder || showHint || showAppt || showManagementModal || showPasswordModal) return;
    
    loadTurnBoard();
    const interval = setInterval(loadTurnBoard, 5000); // Changed from 3s to 5s
    return () => clearInterval(interval);
  }, [loadTurnBoard, showReorder, showHint, showAppt, showManagementModal, showPasswordModal]);

  // Blink animation: 2s on -> 1s off -> 2s on -> 1s off -> 1s on = 7s total
  useEffect(() => {
    if (!highlightedId) {
      setBlinkPhase(0);
      return;
    }
    
    // Phase 0: pink on for 2 seconds
    const timer1 = setTimeout(() => setBlinkPhase(1), 2000);
    // Phase 1: off for 1 second
    const timer2 = setTimeout(() => setBlinkPhase(2), 3000);
    // Phase 2: pink on for 2 seconds
    const timer3 = setTimeout(() => setBlinkPhase(3), 5000);
    // Phase 3: off for 1 second
    const timer4 = setTimeout(() => setBlinkPhase(4), 6000);
    // Phase 4: pink on for 1 second, then clear
    const timer5 = setTimeout(() => {
      setHighlightedId(null);
      setBlinkPhase(0);
    }, 7000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      clearTimeout(timer5);
    };
  }, [highlightedId]);

  const handleAction = async (entryId, action) => {
    if (loading) return;
    setLoading(true);
    
    try {
      await axios.post(`${API}/action/${entryId}/${action}`);
      if (action === 3) {
        toast.success("📞 Call sent!");
      }
      // Reload immediately for all actions to get correct state from backend
      await loadTurnBoard();
    } catch (e) {
      toast.error("Action failed");
      loadTurnBoard();
    }
    setLoading(false);
  };

  const handleUndo = async () => {
    if (loading || historyCount === 0) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/undo`);
      if (res.data.success) {
        toast.success("Undone!");
        await loadTurnBoard();
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error("Undo failed");
    }
    setLoading(false);
  };

  const handleHintSubmit = async (services) => {
    try {
      const res = await axios.post(`${API}/find-tech`, { services });
      if (res.data.success) {
        const tech = res.data.tech;
        toast.success(`Found: ${tech.name} for ${tech.matchedService}`);
        setHighlightedId(tech.id);
        setBlinkPhase(0);
        if (res.data.remaining?.length > 0) {
          setPendingServices(prev => ({ ...prev, [tech.name]: res.data.remaining }));
          setTimeout(() => {
            setPendingServices(prev => {
              const { [tech.name]: _, ...rest } = prev;
              return rest;
            });
          }, 300000);
        }
        setShowHint(false);
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error("Failed to find technician");
    }
  };

  const openWithPassword = (type) => {
    setPendingAction(type);
    if (type === 'reorder') {
      setCurrentPassword(REORDER_PASSWORD);
    } else {
      setCurrentPassword(ADMIN_PASSWORD);
    }
    setShowPasswordModal(true);
  };

  const handlePasswordSuccess = () => {
    setShowPasswordModal(false);
    if (pendingAction === 'management') {
      setShowManagementModal(true);
    } else if (pendingAction === 'reorder') {
      setShowReorder(true);
    } else if (pendingAction === 'service') {
      // Service modal được render riêng, chỉ cần giữ pendingAction = 'service'
      // Không setPendingAction(null) để modal vẫn mở
      return;
    }
    setPendingAction(null);
  };

  const handleApptClick = (entry) => {
    setSelectedEntry(entry);
    setShowAppt(true);
  };

  const handleManualReset = async () => {
    if (resetPassword !== '1510') {
      toast.error('Mật khẩu không đúng!');
      return;
    }
    
    try {
      await axios.post(`${API}/reset-board`, { password: resetPassword });
      toast.success('Board đã reset thủ công!');
      setShowManualReset(false);
      setResetPassword('');
      await loadTurnBoard();
    } catch (e) {
      toast.error('Lỗi khi reset board');
    }
  };

  return (
    <div className="min-h-screen bg-zen-bg">
      <div className="min-h-screen">
        <Toaster position="top-center" richColors />
        
        <header className="bg-zen-primary sticky top-0 z-40 px-3 py-2 shadow-sm">
          <div className="max-w-4xl mx-auto flex justify-between items-center gap-2">
            <h1 className="font-serif text-base text-white tracking-wide whitespace-nowrap">Zen Nails Salon</h1>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setShowClockIn(true)} className="h-8 w-8 bg-zen-cal-green text-white rounded-lg flex items-center justify-center hover:opacity-90 active:scale-95 transition-all shadow-sm" data-testid="open-clockin-btn" title="Clock in a tech">
                <UserPlus className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setShowManualReset(true)}
                className="h-8 w-8 bg-zen-cal-red text-white rounded-lg flex items-center justify-center hover:opacity-90 active:scale-95 transition-all shadow-sm"
                title="Manual Reset (1510)"
                data-testid="manual-reset-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => openWithPassword('reorder')} className="h-8 w-8 bg-zen-ink/80 text-white rounded-lg flex items-center justify-center hover:bg-zen-ink active:scale-95 transition-all shadow-sm" data-testid="reorder-btn" title="Reorder Turn">
                <ArrowUpDown className="w-3.5 h-3.5" />
              </button>
              <button onClick={handleUndo} disabled={historyCount === 0 || loading} className="relative h-8 w-8 bg-zen-cal-red/90 text-white rounded-lg flex items-center justify-center disabled:opacity-40 hover:opacity-90 active:scale-95 transition-all shadow-sm" data-testid="undo-btn">
                <Undo2 className="w-3.5 h-3.5" />
                {historyCount > 0 && <span className="absolute -top-1 -right-1 bg-zen-gold text-zen-ink text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{historyCount}</span>}
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-2 py-2">
          <div className="space-y-1" data-testid="turn-board-list">
            {turnBoard.length === 0 ? (
              <div className="text-center py-16 text-[#8B7355]">
                <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No one clocked in yet</p>
                <p className="text-sm mt-1">Staff will clock in from their phones</p>
              </div>
            ) : (
              turnBoard.map((entry, index) => {
                const appt = appointments.find(a => a.entry_id === entry.id);
                return (
                  <TechCard
                    key={entry.id}
                    entry={entry}
                    index={index}
                    isHighlighted={highlightedId === entry.id}
                    blinkPhase={blinkPhase}
                    onAction={handleAction}
                    pending={pendingServices[entry.name]}
                    appointment={appt}
                    onApptClick={handleApptClick}
                    walkinFlashName={walkinFlashName}
                    onConfirmAppt={async (bookingId, customer) => {
                      try {
                        await api.post(`/admin/bookings/${bookingId}/confirm`);
                        toast.success(`Confirmed ${customer || "appointment"}`);
                        // Optimistic: only mark this entry's appt-status as
                        // 'confirmed' so the ✓ button hides immediately. The
                        // pink-blink itself stays on until owner presses
                        // Walk/Appt or 5 min past start (per owner spec).
                        setTurnBoard((tb) => tb.map((e) =>
                          e.next_appointment_booking_id === bookingId
                            ? { ...e, next_appointment_status: "confirmed" }
                            : e
                        ));
                        loadTurnBoard();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Confirm failed");
                      }
                    }}
                  />
                );
              })
            )}
          </div>
        </main>

        <ServiceHintPopup isOpen={showHint} onClose={() => setShowHint(false)} onSubmit={handleHintSubmit} />
        <AppointmentModal isOpen={showAppt} onClose={() => setShowAppt(false)} entry={selectedEntry} onSet={loadTurnBoard} />
        <ServiceManagement isOpen={pendingAction === 'service' && !showPasswordModal} onClose={() => setPendingAction(null)} />
        <ManagementModal 
          isOpen={showManagementModal} 
          onClose={() => setShowManagementModal(false)} 
          tab={managementTab} 
          onTabChange={setManagementTab}
          turnBoard={turnBoard}
          onReorder={loadTurnBoard}
        />
        <ReorderModal isOpen={showReorder} onClose={() => setShowReorder(false)} turnBoard={turnBoard} onReorder={loadTurnBoard} />
        <PasswordModal 
          isOpen={showPasswordModal} 
          onClose={() => { setShowPasswordModal(false); setPendingAction(null); }}
          onSuccess={handlePasswordSuccess}
          title={pendingAction === 'management' ? 'Management' : pendingAction === 'service' ? 'Service Management' : pendingAction === 'reorder' ? 'Reorder Turn' : 'Access'}
          password={currentPassword}
        />

        {/* Clock-in Modal — quick clock-in by pager # right from Turn Board */}
        {showClockIn && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl border-4 border-zen-cal-green overflow-hidden">
              <div className="bg-zen-cal-green px-5 py-4 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-white" />
                  <span className="font-bold text-white text-lg">Clock-in Tech</span>
                </div>
                <button onClick={() => { setShowClockIn(false); setClockInPager(''); }} className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-zen-muted mb-3 text-center">Enter the technician's pager number</p>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={clockInPager}
                  onChange={(e) => setClockInPager(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleClockInSubmit(); }}
                  placeholder="e.g. 101"
                  autoFocus
                  className="w-full h-14 px-4 text-2xl text-center font-bold bg-zen-bg border-2 border-zen-border rounded-xl focus:border-zen-cal-green focus:outline-none tracking-widest"
                  data-testid="clockin-pager-input"
                />
                <button
                  onClick={handleClockInSubmit}
                  disabled={clockInBusy || !clockInPager.trim()}
                  className="mt-4 w-full h-12 bg-zen-cal-green text-white rounded-xl font-bold disabled:opacity-50 hover:opacity-90 active:scale-95 transition-all"
                  data-testid="clockin-submit-btn">
                  {clockInBusy ? 'Clocking in…' : 'Clock In'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manual Reset Modal */}
        {showManualReset && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl border-4 border-[#C41E3A]">
              <div className="bg-gradient-to-r from-[#C41E3A] to-[#8B0000] px-5 py-4 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Delete className="w-5 h-5 text-white" />
                  <span className="font-bold text-white text-lg">Reset Thủ Công</span>
                </div>
                <button onClick={() => { setShowManualReset(false); setResetPassword(''); }} className="w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-6">
                <p className="text-center text-[#5C4A32] font-semibold mb-2">
                  Reset toàn bộ board?
                </p>
                <p className="text-center text-sm text-[#8B7355] mb-4">
                  Mọi thông tin sẽ bị xóa!
                </p>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#5C4A32] mb-2">
                    Nhập mật khẩu:
                  </label>
                  <input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleManualReset()}
                    className="w-full px-4 py-3 border-2 border-[#D4B896] rounded-xl focus:border-[#C41E3A] outline-none text-center text-2xl tracking-widest"
                    placeholder="••••"
                    maxLength={4}
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowManualReset(false); setResetPassword(''); }}
                    className="flex-1 py-3 bg-[#F5E6D3] text-[#6B5234] rounded-xl font-semibold hover:bg-[#E8D9C5]"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleManualReset}
                    disabled={!resetPassword}
                    className="flex-1 py-3 bg-[#C41E3A] text-white rounded-xl font-semibold hover:bg-[#8B0000] disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Delete className="w-4 h-4" />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ APP ROUTER ============
// ============ BOOKING SYSTEM IMPORTS ============
import { AuthProvider, useAuth } from "@/lib/auth";
import { I18nProvider } from "@/lib/i18n";
import { Navigate } from "react-router-dom";
import BookingPage from "@/pages/BookingPage";
import BookingConfirmation from "@/pages/BookingConfirmation";
import LoginPage from "@/pages/LoginPage";
import OwnerDashboard from "@/pages/OwnerDashboard";
import StaffDashboard from "@/pages/StaffDashboard";
import ManageServices from "@/pages/ManageServices";
import ManageStaff from "@/pages/ManageStaff";
import ManageUsers from "@/pages/ManageUsers";
import CRMPage from "@/pages/CRMPage";
import TrashPage from "@/pages/TrashPage";
import BlacklistPage from "@/pages/BlacklistPage";
import ActivityLogPage from "@/pages/ActivityLogPage";
import BusinessHoursPage from "@/pages/BusinessHoursPage";
import BackupPage from "@/pages/BackupPage";
import ReviewsPage from "@/pages/ReviewsPage";
import MobileBookings from "@/pages/MobileBookings";

function ProtectedRoute({ children, allow }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-6 h-6 rounded-full border-2 border-zen-primary border-t-transparent animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/login" replace />;
  return children;
}

// Owner-set passcode gating the public /turn-board TV display so random people
// who guess the URL can't tamper with the board. Forever-remember once entered.
// Skipped when used inside the admin sidebar iframe (?embed=1 param).
const TURN_BOARD_PASSCODE = "2026";
function TurnBoardGate() {
  const params = new URLSearchParams(window.location.search);
  const embed = params.get("embed") === "1";
  const [unlocked, setUnlocked] = useState(
    () => embed || localStorage.getItem("zen_tb_unlocked") === "1"
  );
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  if (unlocked) return <TurnBoard />;
  const submit = (e) => {
    e.preventDefault();
    if (pwd.trim() === TURN_BOARD_PASSCODE) {
      localStorage.setItem("zen_tb_unlocked", "1");
      setUnlocked(true);
    } else {
      setErr("Wrong passcode");
      setPwd("");
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-zen-surface px-6" data-testid="tb-gate">
      <form onSubmit={submit} className="bg-white rounded-3xl shadow-xl p-10 w-full max-w-sm space-y-5">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-zen-muted">Turn Board</div>
          <div className="font-serif text-3xl text-zen-ink mt-1">Enter passcode</div>
        </div>
        <input
          type="password"
          inputMode="numeric"
          autoFocus
          value={pwd}
          onChange={(e) => { setPwd(e.target.value); setErr(""); }}
          placeholder="••••"
          className="w-full h-12 px-4 rounded-xl bg-zen-surface border-0 text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-zen-primary"
          data-testid="tb-gate-input"
        />
        {err && <div className="text-sm text-red-600" data-testid="tb-gate-error">{err}</div>}
        <button type="submit" className="w-full h-12 rounded-full bg-zen-primary text-white font-semibold" data-testid="tb-gate-submit">
          Unlock
        </button>
      </form>
    </div>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" />
          <Routes>
            {/* Public booking — customer-facing landing */}
            <Route path="/" element={<BookingPage />} />
            <Route path="/booking/:id" element={<BookingConfirmation />} />

            {/* Login */}
            <Route path="/login" element={<LoginPage />} />

            {/* Turn Board — public for TV display, also embedded in admin sidebar via iframe.
                Gate the standalone TV view behind a one-time password so passers-by can't
                visit /turn-board and tamper. Password persists in localStorage forever
                until cleared. Inside admin's iframe we skip the gate (?key= param). */}
            <Route path="/turn-board" element={<TurnBoardGate />} />
            <Route path="/clock-in" element={<TechClockIn />} />

            {/* Admin (owner + receptionist) — Calendar shows turn board sidebar */}
            <Route path="/admin" element={<ProtectedRoute allow={["owner","receptionist"]}><OwnerDashboard /></ProtectedRoute>} />
            <Route path="/admin/services" element={<ProtectedRoute allow={["owner"]}><ManageServices /></ProtectedRoute>} />
            <Route path="/admin/staff" element={<ProtectedRoute allow={["owner"]}><ManageStaff /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute allow={["owner"]}><ManageUsers /></ProtectedRoute>} />
            <Route path="/admin/customers" element={<ProtectedRoute allow={["owner","receptionist"]}><CRMPage /></ProtectedRoute>} />
            <Route path="/admin/trash" element={<ProtectedRoute allow={["owner","receptionist"]}><TrashPage /></ProtectedRoute>} />
            <Route path="/admin/blacklist" element={<ProtectedRoute allow={["owner","receptionist"]}><BlacklistPage /></ProtectedRoute>} />
            <Route path="/admin/activity-log" element={<ProtectedRoute allow={["owner"]}><ActivityLogPage /></ProtectedRoute>} />
            <Route path="/admin/business-hours" element={<ProtectedRoute allow={["owner"]}><BusinessHoursPage /></ProtectedRoute>} />
            <Route path="/admin/backup" element={<ProtectedRoute allow={["owner"]}><BackupPage /></ProtectedRoute>} />
            <Route path="/admin/reviews" element={<ProtectedRoute allow={["owner","receptionist"]}><ReviewsPage /></ProtectedRoute>} />
            {/* Mobile-only bookings monitor — owner can share zen123/12345678910
                to family or front desk for read+confirm+checkin without exposing
                the full admin panel. */}
            <Route path="/mobile-bookings" element={<ProtectedRoute allow={["owner","receptionist"]}><MobileBookings /></ProtectedRoute>} />
            <Route path="/staff" element={<ProtectedRoute allow={["staff"]}><StaffDashboard /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
